import React, { useState, useEffect } from 'react';
import { Mail, Copy, Check, RefreshCw, Settings, Loader2, ChevronDown, ChevronRight, FileText, AlertCircle, CheckCircle, Paperclip } from 'lucide-react';
import { apiFetch } from '../utils/api';

/** Purpose: Renders email ingest. Inputs: none. Output: React render output (JSX). */
export default function EmailIngest() {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addressRefreshing, setAddressRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsUpdatedAt, setEmailsUpdatedAt] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [emailsError, setEmailsError] = useState('');
  const [addressSuccess, setAddressSuccess] = useState('');
  const STATUS_STYLES = {
    COMPLETED: { label: 'Imported', className: 'bg-green-100 text-green-700' },
    FAILED: { label: 'Failed', className: 'bg-red-100 text-red-700' },
    PROCESSING: { label: 'Processing', className: 'bg-amber-100 text-amber-700' },
    PENDING: { label: 'Queued', className: 'bg-amber-100 text-amber-700' }
  };

  useEffect(() => {
    loadAddress();
    loadEmails();
  }, []);

  useEffect(() => {
    if (!addressSuccess) return undefined;
    const timer = setTimeout(() => setAddressSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [addressSuccess]);

  /** Purpose: Loads address. Inputs: { refresh = false } = {}. Output: Promise with operation result. */
  const loadAddress = async ({ refresh = false } = {}) => {
    try {
      setAddressError('');
      if (refresh) {
        setAddressRefreshing(true);
      }
      const response = await apiFetch('/email-ingest/address');
      if (response.ok) {
        const data = await response.json();
        setAddress(data);
      } else {
        setAddressError('Failed to load your ingest address.');
      }
    } catch (err) {
      console.error('Failed to load address:', err);
      setAddressError('Failed to load your ingest address.');
    } finally {
      if (refresh) {
        setAddressRefreshing(false);
      }
      setLoading(false);
    }
  };

  /** Purpose: Loads emails. Inputs: none. Output: Promise with operation result. */
  const loadEmails = async () => {
    try {
      setEmailsError('');
      setEmailsLoading(true);
      const response = await apiFetch('/email-ingest/emails');
      if (response.ok) {
        const data = await response.json();
        setEmails(data.data || []);
        setEmailsUpdatedAt(new Date());
      } else {
        setEmailsError('Failed to load received emails.');
      }
    } catch (err) {
      console.error('Failed to load emails:', err);
      setEmailsError('Failed to load received emails.');
    } finally {
      setEmailsLoading(false);
    }
  };

  /** Purpose: Executes copy to clipboard. Inputs: none. Output: Promise with operation result. */
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(address.fullEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = address.fullEmail;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /** Purpose: Executes regenerate address. Inputs: none. Output: Promise with operation result. */
  const regenerateAddress = async () => {
    if (!window.confirm('Are you sure? The old email address will stop working immediately.')) {
      return;
    }

    setRegenerating(true);
    setAddressError('');
    setAddressSuccess('');
    try {
      const response = await apiFetch('/email-ingest/regenerate', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setAddress(prev => ({ ...(prev || {}), ...data }));
        setAddressSuccess('New ingest address created.');
      } else {
        let message = 'Failed to regenerate address.';
        try {
          const errorData = await response.json();
          message = errorData.error || message;
        } catch (err) {
          // no-op
        }
        setAddressError(message);
      }
    } catch (err) {
      console.error('Failed to regenerate:', err);
      setAddressError('Failed to regenerate address.');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
        <p className="text-slate-500 mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Address Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">Email Records To Your Vault</h2>
              <p className="text-blue-100 text-sm">Healthcare providers can send records directly to this address</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {addressSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5" />
              <span>{addressSuccess}</span>
            </div>
          )}

          {addressError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start justify-between gap-3">
              <span>{addressError}</span>
              <button
                onClick={() => loadAddress({ refresh: true })}
                className="text-xs text-red-700 hover:text-red-900 font-medium"
                disabled={addressRefreshing}
              >
                {addressRefreshing ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          )}

          {/* Email Address Display */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Your Personal Ingest Address
            </label>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg font-mono text-lg break-all">
                {address?.fullEmail || 'Loading...'}
              </div>
              <button
                onClick={copyToClipboard}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                }`}
                disabled={!address?.fullEmail || addressRefreshing}
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Share this address with your healthcare providers or forward emails containing medical records to this address.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{address?.emailsReceived || 0}</p>
              <p className="text-sm text-slate-500">Emails Received</p>
            </div>
            <div className={`rounded-lg p-4 text-center ${address?.isActive ? 'bg-green-50' : 'bg-amber-50'}`}>
              <p className={`text-2xl font-bold ${address?.isActive ? 'text-green-700' : 'text-amber-700'}`}>
                {address?.isActive ? 'Active' : 'Disabled'}
              </p>
              <p className="text-sm text-slate-500">
                Ingest Status
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">
                {address?.lastReceivedAt
                  ? new Date(address.lastReceivedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
                  : 'Never'}
              </p>
              <p className="text-sm text-slate-500">Last Received</p>
            </div>
          </div>
          {!address?.isActive && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center justify-between gap-3">
              <span>Incoming emails are currently disabled. Re-enable in Settings to receive new records.</span>
              <button
                onClick={() => setShowSettings(true)}
                className="text-xs text-amber-900 hover:text-amber-950 font-medium"
              >
                Open Settings
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
              {showSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <button
              onClick={regenerateAddress}
              disabled={regenerating}
              className="px-4 py-2 border border-amber-200 text-amber-900 rounded-lg font-medium hover:bg-amber-50 flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
              {regenerating ? 'Creating...' : 'New Address'}
            </button>
            <button
              onClick={loadEmails}
              disabled={emailsLoading}
              className="ml-auto px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${emailsLoading ? 'animate-spin' : ''}`} />
              {emailsLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <SettingsPanel address={address} onUpdate={setAddress} />
        )}
      </div>

      {/* Received Emails */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-400" />
            Received Emails
          </h3>
          {emailsUpdatedAt && (
            <span className="text-xs text-slate-500">
              Updated {emailsUpdatedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>

        {emailsError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start justify-between gap-3">
            <span>{emailsError}</span>
            <button
              onClick={loadEmails}
              className="text-xs text-red-700 hover:text-red-900 font-medium"
              disabled={emailsLoading}
            >
              {emailsLoading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        )}

        {emailsLoading ? (
          <div className="text-center py-8 text-slate-500">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-slate-400" />
            <p>Loading emails...</p>
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No emails received yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Send or forward medical records to your ingest address
            </p>
            <div className="mt-4">
              <button
                onClick={loadEmails}
                disabled={emailsLoading}
                className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
              >
                Refresh
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {emails.map(email => (
              <div key={email.id} className="border border-slate-200 rounded-lg p-4 transition-colors hover:border-slate-300 hover:bg-slate-50/50">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{email.subject || '(No subject)'}</p>
                    <p className="text-sm text-slate-500">From: {email.fromAddress}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(email.receivedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const status = STATUS_STYLES[email.status] || { label: email.status || 'Unknown', className: 'bg-slate-100 text-slate-600' };
                      return (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.className}`}>
                          {status.label}
                        </span>
                      );
                    })()}
                    {email.attachmentCount > 0 && (
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Paperclip className="w-4 h-4" />
                        {email.attachmentCount} file(s)
                      </span>
                    )}
                  </div>
                </div>
                {email.recordsCreated?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <p className="text-sm text-slate-500">Records created:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {email.recordsCreated.map(record => (
                        <span key={record.id} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                          {record.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          How to Use
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li>Copy your personal email address above</li>
          <li>Share it with your healthcare provider, or</li>
          <li>Forward emails containing medical records to this address</li>
          <li>Attachments (PDFs, images) are automatically imported as records</li>
        </ol>
        <p className="text-sm text-blue-700 mt-4">
          <strong>Privacy:</strong> Your email address is unique and private. Only you can access records sent to it.
        </p>
      </div>
    </div>
  );
}

// Settings Panel Component
/** Purpose: Renders settings panel. Inputs: { address, onUpdate }. Output: React render output (JSX). */
function SettingsPanel({ address, onUpdate }) {
  const [settings, setSettings] = useState({
    isActive: address?.isActive ?? true,
    allowedSenders: address?.allowedSenders?.join(', ') || '',
    requireKeyword: address?.requireKeyword || '',
    autoCreateRecords: address?.autoCreateRecords ?? true,
    defaultRecordType: address?.defaultRecordType || 'OTHER',
    notifyOnReceive: address?.notifyOnReceive ?? true
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  /** Purpose: Executes save settings. Inputs: none. Output: Promise with operation result. */
  const saveSettings = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const response = await apiFetch('/email-ingest/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          isActive: settings.isActive,
          allowedSenders: settings.allowedSenders
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
          requireKeyword: settings.requireKeyword || null,
          autoCreateRecords: settings.autoCreateRecords,
          defaultRecordType: settings.defaultRecordType,
          notifyOnReceive: settings.notifyOnReceive
        })
      });

      if (response.ok) {
        const updated = await response.json();
        onUpdate({ ...address, ...updated });
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2500);
      } else {
        let message = 'Failed to save settings.';
        try {
          const data = await response.json();
          message = data.error || message;
        } catch (err) {
          // no-op
        }
        setSaveError(message);
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveError('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const RECORD_TYPES = [
    'LAB_RESULTS', 'CONSULTATION', 'IMAGING', 'PRESCRIPTIONS',
    'IMMUNIZATIONS', 'SURGICAL', 'DISCHARGE', 'OTHER'
  ];

  return (
    <div className="border-t border-slate-200 p-6 bg-slate-50">
      <h4 className="font-medium text-slate-900 mb-4">Ingest Settings</h4>

      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{saveError}</span>
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5" />
          <span>Settings updated.</span>
        </div>
      )}
      
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.isActive}
              onChange={(e) => setSettings({ ...settings, isActive: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-slate-700">Accept incoming emails</span>
          </label>
          <p className="text-xs text-slate-500 ml-7">Turn this off to pause ingestion without changing your address.</p>
        </div>

        <div className="space-y-1">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.autoCreateRecords}
              onChange={(e) => setSettings({ ...settings, autoCreateRecords: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-slate-700">Automatically create records from attachments</span>
          </label>
          <p className="text-xs text-slate-500 ml-7">If disabled, attachments stay in your inbox until you upload manually.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Default Record Type
          </label>
          <select
            value={settings.defaultRecordType}
            onChange={(e) => setSettings({ ...settings, defaultRecordType: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            {RECORD_TYPES.map(type => (
              <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">Use this when a sender does not specify a record type.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Allowed Senders (comma-separated emails or domains)
          </label>
          <input
            type="text"
            value={settings.allowedSenders}
            onChange={(e) => setSettings({ ...settings, allowedSenders: e.target.value })}
            placeholder="e.g., @hospital.com, doctor@clinic.com"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">Leave empty to allow all senders</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Required Keyword in Subject
          </label>
          <input
            type="text"
            value={settings.requireKeyword}
            onChange={(e) => setSettings({ ...settings, requireKeyword: e.target.value })}
            placeholder="e.g., MEDCADDY or a secret code"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">Leave empty for no keyword requirement</p>
        </div>

        <div className="space-y-1">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.notifyOnReceive}
              onChange={(e) => setSettings({ ...settings, notifyOnReceive: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-slate-700">Notify me when new emails arrive</span>
          </label>
          <p className="text-xs text-slate-500 ml-7">You will see updates in your activity feed.</p>
        </div>

        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
